import { useState, useMemo } from 'react'

type Page = 'dashboard' | 'admin-dashboard' | 'organisations' | 'assets' | 'dpp-templates' | 'dpp-template-create' | 'dpp-template-edit' | 'product-create' | 'product-edit' | 'iot' | 'esg' | 'workorders' | 'workorder-create' | 'workorder-edit' | 'users' | 'settings' | 'audit-logs' | 'tutorial'

interface TutorialPageProps {
  navigateTo: (page: Page, params?: string) => void
  userRole: string
}

interface TutorialStep {
  title: string
  pill: string
  roles?: string[]
  content: JSX.Element
}

export default function TutorialPage({ navigateTo, userRole }: TutorialPageProps) {
  const [currentStep, setCurrentStep] = useState(0)

  const allSteps: TutorialStep[] = useMemo(() => [
    // Step 1: Welcome (all roles)
    {
      title: 'Welcome to the Ping Group Digital Product Passport Platform',
      pill: 'Welcome',
      content: (
        <div className="space-y-4">
          <p className="text-lg">
            The Ping Group DPP platform tracks the full lifecycle of high-value aviation assets — from manufacture at Thompson Aero Seating in Portadown, through dynamic certification at the Banbridge DTF, line-fit at the Airbus FAL, in-service maintenance, and ultimately end-of-life — with built-in regulatory compliance via Digital Product Passports.
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded p-4">
            <h4 className="font-semibold text-blue-900 mb-2">Platform capabilities:</h4>
            <ul className="list-disc list-inside space-y-1 text-blue-800">
              <li><strong>Product Registry</strong> — Thompson Aero's Vantage family of business- and first-class seats (VantageNOVA, VantageXL, VantageXL+, VantageSOLO, VantageDUO) with full sub-assembly bills of materials</li>
              <li><strong>Digital Product Passports (DPP)</strong> — EU ESPR-aligned passports with QR codes, substance tracking, certification anchors (TSO-C127c, FAR/CS 25.561/.562/.853), carbon footprint data, and public/restricted access tiers</li>
              <li><strong>IoT Monitoring</strong> — Production-line environmental sensors at Portadown, 16g dynamic-test instrumentation at Banbridge, and in-service actuator telemetry on installed suites</li>
              <li><strong>ESG Reporting</strong> — Green Cabin Alliance metrics, Scope 1/2/3 emissions, and CSRD-aligned disclosures across daily, weekly, monthly, quarterly, or yearly periods</li>
              <li><strong>Work Orders</strong> — Coordinate manufacture batches, dynamic certification, FAL deliveries, scheduled cabin inspections, upholstery refresh, and decommission flows</li>
              <li><strong>Multi-Tenancy</strong> — Ping Group (operator), Thompson Aero (manufacturer), Delta Air Lines (asset owner) and AVIC (parent) all isolated with role-based access</li>
            </ul>
          </div>
          <div className="bg-gray-50 rounded p-4">
            <h4 className="font-semibold mb-2">Platform Roles:</h4>
            <div className="grid grid-cols-1 gap-2 text-sm">
              <div className="flex gap-2"><span className="inline-block w-28 font-semibold text-esf-navy shrink-0">SuperAdmin</span><span>Full platform access across all organisations — manages tenants, views global audit logs, and platform-wide statistics</span></div>
              <div className="flex gap-2"><span className="inline-block w-28 font-semibold text-esf-navy shrink-0">OrgAdmin</span><span>Organisation administrator — manages users, settings, and has full access to their organisation's data</span></div>
              <div className="flex gap-2"><span className="inline-block w-28 font-semibold text-esf-navy shrink-0">AssetManager</span><span>Creates and edits assets, DPP templates, work orders, and views IoT/ESG data</span></div>
              <div className="flex gap-2"><span className="inline-block w-28 font-semibold text-esf-navy shrink-0">FieldTech</span><span>Executes work orders in the field, views asset details and IoT devices</span></div>
              <div className="flex gap-2"><span className="inline-block w-28 font-semibold text-esf-navy shrink-0">Analyst</span><span>Read-only access to the product registry, IoT data, and ESG reports for analysis</span></div>
              <div className="flex gap-2"><span className="inline-block w-28 font-semibold text-esf-navy shrink-0">PublicViewer</span><span>Can only view publicly shared passport pages via QR code — no dashboard access</span></div>
            </div>
          </div>
          <p className="text-sm text-gray-600">
            <strong>Note:</strong> Your current role is <span className="font-semibold text-esf-navy">{userRole}</span>. This tutorial only shows steps relevant to your permissions — {userRole === 'SuperAdmin' ? 'as a SuperAdmin you have access to all features.' : 'some platform administration sections may be hidden.'}
          </p>
        </div>
      ),
    },
    // Step 2: Dashboard (all roles)
    {
      title: 'Understanding the Dashboard',
      pill: 'Dashboard',
      content: (
        <div className="space-y-4">
          <p>
            The <strong>Dashboard</strong> is your landing page after login. It provides a real-time snapshot of your organisation's assets and operations.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border rounded-lg p-4 bg-gray-50">
              <h4 className="font-semibold mb-2">KPI Summary Cards</h4>
              <p className="text-sm text-gray-700">
                The top row shows headline numbers: total assets in your registry, how many are in each status (Active, Maintenance, Repair Needed, etc.), pending work orders, and connected IoT devices. These update automatically as data changes.
              </p>
            </div>
            <div className="border rounded-lg p-4 bg-gray-50">
              <h4 className="font-semibold mb-2">Quick Links</h4>
              <p className="text-sm text-gray-700">
                Clickable shortcut cards take you directly to filtered views of the Product Registry. For example, clicking "Repair Needed" opens the registry pre-filtered to that status — saving you from manually setting filters.
              </p>
            </div>
            <div className="border rounded-lg p-4 bg-gray-50">
              <h4 className="font-semibold mb-2">Analytics Charts</h4>
              <p className="text-sm text-gray-700">
                Visual breakdowns of your asset portfolio — status distribution (pie chart), category spread (bar chart), and recent asset activity. These help spot trends like a growing maintenance backlog.
              </p>
            </div>
            <div className="border rounded-lg p-4 bg-gray-50">
              <h4 className="font-semibold mb-2">Recent Assets</h4>
              <p className="text-sm text-gray-700">
                A quick-access list of recently updated assets. Click any item to open its detail modal without leaving the dashboard.
              </p>
            </div>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
            <p className="text-sm text-yellow-800">
              <strong>Tip:</strong> The dashboard content adapts to your role. SuperAdmins also see a separate Platform Overview with cross-organisation metrics.
            </p>
          </div>
          <button
            className="btn btn-secondary"
            onClick={() => navigateTo('dashboard')}
          >
            Go to Dashboard
          </button>
        </div>
      ),
    },
    // Step 3: Platform Administration (SuperAdmin only)
    {
      title: 'Platform Administration',
      pill: 'Platform Admin',
      roles: ['SuperAdmin'],
      content: (
        <div className="space-y-4">
          <p>
            As a <strong>SuperAdmin</strong>, you have three dedicated pages under the <em>Platform</em> section of the sidebar that provide cross-organisation visibility and control.
          </p>
          <div className="space-y-3">
            <div className="border-l-4 border-blue-500 pl-4 py-1">
              <h4 className="font-semibold">1. Platform Overview</h4>
              <p className="text-sm text-gray-700 mb-1">
                An aggregated dashboard showing totals across every tenant: organisations, assets, users, work orders, and IoT devices. Below the summary, a per-organisation breakdown table lets you compare activity levels.
              </p>
              <p className="text-sm text-gray-700">
                Use the organisation selector dropdown to drill into a single tenant's metrics without switching context. Click an organisation row to jump straight to its detail page.
              </p>
            </div>
            <div className="border-l-4 border-green-500 pl-4 py-1">
              <h4 className="font-semibold">2. Organisations</h4>
              <p className="text-sm text-gray-700 mb-1">
                Create, view, edit, and manage tenant organisations. Each organisation is an isolated data boundary — users, assets, and work orders are scoped to a single org.
              </p>
              <p className="text-sm text-gray-700">
                From this page you can: create a new organisation (name, slug, contact details), toggle an organisation's active/disabled state, and click into a detail modal showing full profile information and statistics.
              </p>
            </div>
            <div className="border-l-4 border-purple-500 pl-4 py-1">
              <h4 className="font-semibold">3. Audit Logs</h4>
              <p className="text-sm text-gray-700 mb-1">
                Every significant action in the system (create, update, delete, login, status change) is recorded with a timestamp, actor email, action type, and affected resource. Audit logs are immutable and stored for compliance.
              </p>
              <p className="text-sm text-gray-700">
                Filter logs by: action type, actor (user email), organisation, date range, or free-text search. Results are paginated — click "Load more" to fetch the next page.
              </p>
            </div>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded p-3">
            <p className="text-sm text-amber-800">
              <strong>OrgAdmins</strong> can also view audit logs, but only for actions within their own organisation.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button className="btn btn-secondary" onClick={() => navigateTo('admin-dashboard')}>
              Go to Platform Overview
            </button>
            <button className="btn btn-secondary" onClick={() => navigateTo('organisations')}>
              Go to Organisations
            </button>
            <button className="btn btn-secondary" onClick={() => navigateTo('audit-logs')}>
              Go to Audit Logs
            </button>
          </div>
        </div>
      ),
    },
    // Step 4: User Management (SuperAdmin + OrgAdmin)
    {
      title: 'User Management',
      pill: 'Users',
      roles: ['SuperAdmin', 'OrgAdmin'],
      content: (
        <div className="space-y-4">
          <p>
            The <strong>Users</strong> page lets you manage who has access to the platform and what they can do. Users are scoped to an organisation — OrgAdmins manage their own org's users, while SuperAdmins can manage users across all organisations.
          </p>
          <div className="space-y-3">
            <div className="border-l-4 border-blue-500 pl-4 py-1">
              <h4 className="font-semibold">1. Creating Users</h4>
              <p className="text-sm text-gray-700">
                Click <em>"Add User"</em> to open the user form. Provide an email address, display name, and select a role. The user will be created in your current organisation. SuperAdmins can also specify which organisation the user belongs to.
              </p>
            </div>
            <div className="border-l-4 border-green-500 pl-4 py-1">
              <h4 className="font-semibold">2. Assigning Roles</h4>
              <p className="text-sm text-gray-700">
                Roles control which sidebar pages are visible and which API actions are permitted. Choose carefully — an AssetManager can create and edit assets but cannot manage users, while a FieldTech can only execute work orders and view asset details.
              </p>
            </div>
            <div className="border-l-4 border-purple-500 pl-4 py-1">
              <h4 className="font-semibold">3. Enable/Disable &amp; Soft Delete</h4>
              <p className="text-sm text-gray-700">
                Disable a user to temporarily revoke access without losing their data. Soft-delete a user to remove them from the active list — toggle <em>"Show deleted"</em> to see and restore soft-deleted users. All user changes are recorded in the audit log.
              </p>
            </div>
            <div className="border-l-4 border-orange-500 pl-4 py-1">
              <h4 className="font-semibold">4. Password Reset</h4>
              <p className="text-sm text-gray-700">
                Reset a user's password from the user list — click the key icon on the user's row. This generates a new temporary password that you can share with the user.
              </p>
            </div>
          </div>
          <div className="bg-gray-50 rounded p-4">
            <h4 className="font-semibold mb-2">What each role can access:</h4>
            <div className="overflow-x-auto">
              <table className="text-sm w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-1 pr-3 font-semibold">Role</th>
                    <th className="text-center py-1 px-2">Assets</th>
                    <th className="text-center py-1 px-2">DPP</th>
                    <th className="text-center py-1 px-2">IoT</th>
                    <th className="text-center py-1 px-2">ESG</th>
                    <th className="text-center py-1 px-2">Work Orders</th>
                    <th className="text-center py-1 px-2">Users</th>
                    <th className="text-center py-1 px-2">Platform</th>
                  </tr>
                </thead>
                <tbody className="text-gray-700">
                  <tr className="border-b"><td className="py-1 pr-3 font-medium">SuperAdmin</td><td className="text-center">{'\u2713'}</td><td className="text-center">{'\u2713'}</td><td className="text-center">{'\u2713'}</td><td className="text-center">{'\u2713'}</td><td className="text-center">{'\u2713'}</td><td className="text-center">{'\u2713'}</td><td className="text-center">{'\u2713'}</td></tr>
                  <tr className="border-b"><td className="py-1 pr-3 font-medium">OrgAdmin</td><td className="text-center">{'\u2713'}</td><td className="text-center">{'\u2713'}</td><td className="text-center">{'\u2713'}</td><td className="text-center">{'\u2713'}</td><td className="text-center">{'\u2713'}</td><td className="text-center">{'\u2713'}</td><td className="text-center">{'\u2014'}</td></tr>
                  <tr className="border-b"><td className="py-1 pr-3 font-medium">AssetManager</td><td className="text-center">{'\u2713'}</td><td className="text-center">{'\u2713'}</td><td className="text-center">{'\u2713'}</td><td className="text-center">{'\u2713'}</td><td className="text-center">{'\u2713'}</td><td className="text-center">{'\u2014'}</td><td className="text-center">{'\u2014'}</td></tr>
                  <tr className="border-b"><td className="py-1 pr-3 font-medium">FieldTech</td><td className="text-center">{'\u2713'}</td><td className="text-center">{'\u2014'}</td><td className="text-center">{'\u2713'}</td><td className="text-center">{'\u2014'}</td><td className="text-center">{'\u2713'}</td><td className="text-center">{'\u2014'}</td><td className="text-center">{'\u2014'}</td></tr>
                  <tr><td className="py-1 pr-3 font-medium">Analyst</td><td className="text-center">{'\u2713'}</td><td className="text-center">{'\u2014'}</td><td className="text-center">{'\u2713'}</td><td className="text-center">{'\u2713'}</td><td className="text-center">{'\u2014'}</td><td className="text-center">{'\u2014'}</td><td className="text-center">{'\u2014'}</td></tr>
                </tbody>
              </table>
            </div>
          </div>
          <button className="btn btn-secondary" onClick={() => navigateTo('users')}>
            Go to User Management
          </button>
        </div>
      ),
    },
    // Step 5: Product Registry (asset access roles)
    {
      title: 'Managing the Product Registry',
      pill: 'Products',
      roles: ['SuperAdmin', 'OrgAdmin', 'AssetManager', 'FieldTech', 'Analyst'],
      content: (
        <div className="space-y-4">
          <p>
            The <strong>Product Registry</strong> is the core of the platform — a searchable catalogue of every street furniture asset your organisation manages.
          </p>
          <div className="space-y-3">
            <div className="border-l-4 border-blue-500 pl-4 py-1">
              <h4 className="font-semibold">1. Search and Filter</h4>
              <p className="text-sm text-gray-700">
                Use the search bar to find assets by name, serial number, or location. Combine with dropdown filters for <strong>status</strong> and <strong>category</strong> to narrow results. The global search in the top header also searches across assets, work orders, users, and IoT devices simultaneously.
              </p>
            </div>
            <div className="border-l-4 border-green-500 pl-4 py-1">
              <h4 className="font-semibold">2. View Asset Details</h4>
              <p className="text-sm text-gray-700">
                Click any row to open the detail modal. This shows the asset's full profile: name, category, status, condition rating (Excellent/Good/Fair/Poor), location coordinates, manufacturer, materials, certifications, carbon footprint, recyclability score, expected lifespan, and warranty dates. You can also see linked work orders and IoT devices from this modal.
              </p>
            </div>
            <div className="border-l-4 border-purple-500 pl-4 py-1">
              <h4 className="font-semibold">3. Create and Edit Assets</h4>
              <p className="text-sm text-gray-700">
                Click <em>"Add Product"</em> to open the product form. Required fields are name, category, and status. Optional fields include manufacturer, country of origin, condition, notes, maintenance dates, and sustainability metrics. Editing an existing asset uses the same form with pre-populated values.
              </p>
            </div>
            <div className="border-l-4 border-orange-500 pl-4 py-1">
              <h4 className="font-semibold">4. Soft Delete and Restore</h4>
              <p className="text-sm text-gray-700">
                Deleting an asset sets a <code className="text-xs bg-gray-100 px-1 rounded">deletedAt</code> timestamp rather than permanently removing it. Toggle <em>"Show deleted"</em> at the top of the list to reveal soft-deleted records, then click <em>"Restore"</em> to bring them back.
              </p>
            </div>
          </div>
          <div className="bg-gray-50 rounded p-4">
            <h4 className="font-semibold mb-2">Asset Categories:</h4>
            <div className="flex flex-wrap gap-2 text-sm">
              {['Seating', 'Litter & Recycling', 'Shelters', 'Cycle', 'Bollards & Barriers', 'Signage & Display', 'Solar & Power', 'Planters', 'Lighting', 'Other'].map(cat => (
                <span key={cat} className="px-2 py-1 bg-blue-50 text-blue-800 rounded border border-blue-200">{cat}</span>
              ))}
            </div>
          </div>
          <div className="space-y-2 bg-gray-50 rounded p-4">
            <h4 className="font-semibold">Lifecycle Statuses:</h4>
            <div className="flex flex-wrap gap-2 items-center text-sm">
              <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full">PENDING_INSTALLATION</span>
              <span>→</span>
              <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full">ACTIVE</span>
              <span>→</span>
              <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full">MAINTENANCE</span>
              <span>/</span>
              <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full">REPAIR_NEEDED</span>
              <span>→</span>
              <span className="px-3 py-1 bg-gray-200 text-gray-700 rounded-full">DECOMMISSIONED</span>
            </div>
          </div>
          <button className="btn btn-secondary" onClick={() => navigateTo('assets')}>
            Go to Product Registry
          </button>
        </div>
      ),
    },
    // Step 6: Digital Product Passports (DPP template access roles)
    {
      title: 'Digital Product Passports (DPP)',
      pill: 'DPP',
      roles: ['SuperAdmin', 'OrgAdmin', 'AssetManager'],
      content: (
        <div className="space-y-4">
          <p>
            <strong>Digital Product Passports</strong> are structured, machine-readable records attached to assets. They're designed to meet the EU's <strong>Ecodesign for Sustainable Products Regulation (ESPR)</strong> requirements and enable supply chain transparency via QR codes.
          </p>
          <div className="space-y-3">
            <div className="border-l-4 border-blue-500 pl-4 py-1">
              <h4 className="font-semibold">DPP Templates</h4>
              <p className="text-sm text-gray-700">
                Templates define the structure of a passport — which fields are required, what data to collect. Create templates for different product types (e.g. a solar bench passport vs. a shelter passport). Templates are reusable across many assets. Manage them from the <em>DPP Templates</em> page.
              </p>
            </div>
            <div className="border-l-4 border-green-500 pl-4 py-1">
              <h4 className="font-semibold">Passport Data &amp; Compliance</h4>
              <p className="text-sm text-gray-700 mb-1">
                Each passport can capture a wide range of ESPR-mandated data:
              </p>
              <ul className="list-disc list-inside text-sm text-gray-700 space-y-0.5 ml-2">
                <li><strong>Identification</strong> — GS1 GTIN, serial numbers, batch codes, data carrier (QR/RFID/NFC)</li>
                <li><strong>Economic operators</strong> — Manufacturer, importer, distributor with GLN and VAT details</li>
                <li><strong>Substances of concern</strong> — SVHC, CLP hazardous, circularity inhibitors with CAS/EC numbers and concentrations</li>
                <li><strong>Carbon footprint</strong> — Per-lifecycle-phase CO2e data (raw materials, manufacturing, distribution, use, end-of-life)</li>
                <li><strong>Circularity</strong> — Recyclability score, recycled content, repairability score, disassembly instructions, spare part availability</li>
                <li><strong>Documents</strong> — Attach compliance certificates, test reports, and declarations with access tier control (Public, Legitimate Interest, Restricted)</li>
              </ul>
            </div>
            <div className="border-l-4 border-purple-500 pl-4 py-1">
              <h4 className="font-semibold">Battery Passports</h4>
              <p className="text-sm text-gray-700">
                For battery-containing assets, a specialised section tracks EU Battery Regulation data: chemistry type (lithium-ion, NiMH, lead-acid, etc.), rated capacity, cycle life, state of health, and end-of-life collection/recycling instructions.
              </p>
            </div>
            <div className="border-l-4 border-orange-500 pl-4 py-1">
              <h4 className="font-semibold">QR Codes &amp; Public Passport View</h4>
              <p className="text-sm text-gray-700">
                Every passport receives a unique ID and QR code. When scanned (or visited via URL), the <strong>public passport page</strong> renders without requiring authentication — showing product details, sustainability metrics, and compliance data. This is the page consumers and regulators see when they scan a product in the field.
              </p>
            </div>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
            <p className="text-sm text-yellow-800">
              <strong>Workflow:</strong> Create a DPP template first, then link it to assets. When an asset has a linked DPP, its passport data is populated through the template-driven form and becomes accessible via the QR code.
            </p>
          </div>
          <button className="btn btn-secondary" onClick={() => navigateTo('dpp-templates')}>
            Go to DPP Templates
          </button>
        </div>
      ),
    },
    // Step 7: IoT Device Monitoring
    {
      title: 'IoT Device Monitoring',
      pill: 'IoT',
      roles: ['SuperAdmin', 'OrgAdmin', 'AssetManager', 'FieldTech', 'Analyst'],
      content: (
        <div className="space-y-4">
          <p>
            The <strong>IoT Dashboard</strong> provides real-time monitoring of sensors and devices attached to your street furniture assets. Each device reports status, battery level, signal strength, and periodic sensor readings.
          </p>
          <div className="space-y-3">
            <div className="border rounded-lg p-4 bg-gradient-to-r from-blue-50 to-blue-100">
              <h4 className="font-semibold mb-2">Device List &amp; Filtering</h4>
              <p className="text-sm text-gray-700">
                The main table shows all registered devices with sortable columns: device ID, type, status, battery level, signal strength, and last seen timestamp. Filter by <strong>device type</strong> or <strong>status</strong> (Online, Offline, Maintenance, Error), or use the search bar. Sort by any column — e.g. sort by battery level to find devices that need attention.
              </p>
            </div>
            <div className="border rounded-lg p-4 bg-gradient-to-r from-green-50 to-green-100">
              <h4 className="font-semibold mb-2">Sensor Readings</h4>
              <p className="text-sm text-gray-700">
                Click a device to open its detail modal and view historical sensor readings. Each reading includes a timestamp, value, and unit. Readings are stored with a configurable TTL (time-to-live) for automatic expiry of old data.
              </p>
            </div>
            <div className="border rounded-lg p-4 bg-gradient-to-r from-orange-50 to-orange-100">
              <h4 className="font-semibold mb-2">Device-to-Asset Linking</h4>
              <p className="text-sm text-gray-700">
                Every IoT device is linked to a specific asset via its <code className="text-xs bg-white/60 px-1 rounded">assetId</code> field. From the device detail modal you can jump to the linked asset, and from an asset's detail modal you can see its connected devices.
              </p>
            </div>
            <div className="border rounded-lg p-4 bg-gradient-to-r from-purple-50 to-purple-100">
              <h4 className="font-semibold mb-2">Export &amp; Saved Reports</h4>
              <p className="text-sm text-gray-700">
                Export the current filtered device list to CSV. Previously generated reports are saved and can be re-downloaded from the <em>"Saved Reports"</em> panel.
              </p>
            </div>
          </div>
          <div className="bg-gray-50 rounded p-4">
            <h4 className="font-semibold mb-2">Supported Device Types:</h4>
            <div className="flex flex-wrap gap-2 text-sm">
              {['Occupancy Sensor', 'Solar Monitor', 'Waste Level', 'Air Quality', 'Temperature', 'Humidity', 'Noise Level', 'Light Sensor', 'Structural Monitor'].map(dt => (
                <span key={dt} className="px-2 py-1 bg-blue-50 text-blue-800 rounded border border-blue-200">{dt}</span>
              ))}
            </div>
          </div>
          <button className="btn btn-secondary" onClick={() => navigateTo('iot')}>
            Go to IoT Dashboard
          </button>
        </div>
      ),
    },
    // Step 8: ESG Sustainability Reporting
    {
      title: 'ESG Sustainability Reporting',
      pill: 'ESG',
      roles: ['SuperAdmin', 'OrgAdmin', 'AssetManager', 'Analyst'],
      content: (
        <div className="space-y-4">
          <p>
            The <strong>ESG Reports</strong> page tracks your organisation's environmental, social, and governance sustainability metrics. Data is recorded per-period and linked to specific assets for traceability.
          </p>
          <div className="space-y-3">
            <div className="border-l-4 border-green-500 pl-4 py-1">
              <h4 className="font-semibold">Environmental Metrics</h4>
              <p className="text-sm text-gray-700">
                Each ESG record captures four key environmental indicators:
              </p>
              <ul className="list-disc list-inside text-sm text-gray-700 ml-2 mt-1 space-y-0.5">
                <li><strong>Carbon saved</strong> (kg CO2e) — emissions avoided by using sustainable street furniture</li>
                <li><strong>Energy generated</strong> (kWh) — from solar panels, kinetic harvesters, or other on-asset generation</li>
                <li><strong>Waste collected</strong> (kg) — captured by smart bins, recycling units, and litter infrastructure</li>
                <li><strong>Recycling rate</strong> (%) — proportion of collected waste that was recycled</li>
              </ul>
            </div>
            <div className="border-l-4 border-blue-500 pl-4 py-1">
              <h4 className="font-semibold">Social &amp; Governance Metrics</h4>
              <p className="text-sm text-gray-700">
                Beyond environmental data, records also track <strong>public usage count</strong> (how many people used the asset in the period) and <strong>maintenance adherence</strong> (percentage of scheduled maintenance completed on time).
              </p>
            </div>
            <div className="border-l-4 border-purple-500 pl-4 py-1">
              <h4 className="font-semibold">Periods &amp; Sources</h4>
              <p className="text-sm text-gray-700">
                Metrics are recorded at configurable intervals: Daily, Weekly, Monthly, Quarterly, or Yearly. Each record is tagged with its data source — <strong>Automated</strong> (from IoT sensors), <strong>Manual</strong> (entered by staff), or <strong>Imported</strong> (bulk upload). Filter by source to distinguish automated readings from manual entries.
              </p>
            </div>
            <div className="border-l-4 border-orange-500 pl-4 py-1">
              <h4 className="font-semibold">Filtering, Sorting &amp; Export</h4>
              <p className="text-sm text-gray-700">
                Filter by data source and date range. Sort by any metric column (period start, carbon saved, energy generated, waste collected, or recycling rate) in ascending or descending order. Export the filtered results to <strong>CSV</strong> for regulatory submissions or further analysis. Previously generated exports are saved and accessible from the <em>"Saved Reports"</em> panel.
              </p>
            </div>
          </div>
          <button className="btn btn-secondary" onClick={() => navigateTo('esg')}>
            Go to ESG Reports
          </button>
        </div>
      ),
    },
    // Step 9: Work Orders
    {
      title: 'Work Orders',
      pill: 'Work Orders',
      roles: ['SuperAdmin', 'OrgAdmin', 'AssetManager', 'FieldTech'],
      content: (
        <div className="space-y-4">
          <p>
            <strong>Work Orders</strong> coordinate all field operations — from routine inspections to emergency repairs. Each work order is linked to a specific asset and assigned to a technician.
          </p>
          <div className="space-y-3">
            <div className="border-l-4 border-blue-500 pl-4 py-1">
              <h4 className="font-semibold">1. Creating a Work Order</h4>
              <p className="text-sm text-gray-700">
                Click <em>"Create Work Order"</em> or use the shortcut from an asset's detail modal. Fill in: <strong>title</strong>, <strong>type</strong>, <strong>priority</strong>, description, assigned technician, and scheduled date. The asset link is required — this ties the work back to a specific piece of street furniture.
              </p>
            </div>
            <div className="border-l-4 border-green-500 pl-4 py-1">
              <h4 className="font-semibold">2. Filtering &amp; Search</h4>
              <p className="text-sm text-gray-700">
                The work orders list supports filtering by <strong>status</strong>, <strong>priority</strong>, <strong>type</strong>, <strong>assigned asset</strong>, and <strong>assignee</strong>. Combine with the search bar to find specific work orders quickly. Toggle <em>"Show deleted"</em> to view cancelled or soft-deleted orders.
              </p>
            </div>
            <div className="border-l-4 border-purple-500 pl-4 py-1">
              <h4 className="font-semibold">3. Updating &amp; Completing</h4>
              <p className="text-sm text-gray-700">
                Edit a work order to change its status, reassign it, update the scheduled date, or add notes. When the work is done, set the status to <em>Completed</em> — a completion timestamp is recorded automatically. Add notes to document what was done, parts used, or issues found.
              </p>
            </div>
          </div>
          <div className="bg-gray-50 rounded p-4">
            <h4 className="font-semibold mb-2">Work Order Types:</h4>
            <div className="flex flex-wrap gap-2 text-sm">
              {['Inspection', 'Maintenance', 'Repair', 'Installation', 'Decommission', 'Sensor Replacement'].map(t => (
                <span key={t} className="px-2 py-1 bg-blue-50 text-blue-800 rounded border border-blue-200">{t}</span>
              ))}
            </div>
          </div>
          <div className="space-y-2 bg-gray-50 rounded p-4">
            <h4 className="font-semibold">Status Workflow:</h4>
            <div className="flex flex-wrap gap-2 items-center text-sm">
              <span className="px-3 py-1 bg-gray-200 rounded-full">PENDING</span>
              <span>→</span>
              <span className="px-3 py-1 bg-blue-200 rounded-full">SCHEDULED</span>
              <span>→</span>
              <span className="px-3 py-1 bg-yellow-200 rounded-full">IN_PROGRESS</span>
              <span>→</span>
              <span className="px-3 py-1 bg-green-200 rounded-full">COMPLETED</span>
            </div>
            <p className="text-xs text-gray-600 mt-1">
              Work orders can also be set to <strong>ON_HOLD</strong> (paused, awaiting parts or approval) or <strong>CANCELLED</strong>. Every status change is recorded with a timestamp.
            </p>
          </div>
          <div className="bg-gray-50 rounded p-4">
            <h4 className="font-semibold mb-2">Priority Levels:</h4>
            <div className="flex flex-wrap gap-2 text-sm">
              <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full">LOW</span>
              <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full">MEDIUM</span>
              <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full">HIGH</span>
              <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full">URGENT</span>
            </div>
          </div>
          <button className="btn btn-secondary" onClick={() => navigateTo('workorders')}>
            Go to Work Orders
          </button>
        </div>
      ),
    },
    // Step 10: Getting Started Checklist (all roles)
    {
      title: 'Getting Started Checklist',
      pill: 'Checklist',
      content: (
        <div className="space-y-4">
          <p className="text-lg font-medium">
            You've completed the tutorial! Use this checklist to explore the platform hands-on. Tick items off as you go — click <em>"Go"</em> to jump straight to each page.
          </p>
          <div className="space-y-3">
            <ChecklistItem
              label="Explore the Dashboard"
              description="Review KPI summary cards, click a quick link to see a pre-filtered asset view, and browse the analytics charts"
              onClick={() => navigateTo('dashboard')}
            />
            {(['SuperAdmin', 'OrgAdmin', 'AssetManager', 'FieldTech', 'Analyst'].includes(userRole)) && (
              <ChecklistItem
                label="Browse the Product Registry"
                description="Open the Product Registry, try searching for an asset by name, and click a row to open its detail modal"
                onClick={() => navigateTo('assets')}
              />
            )}
            {(['SuperAdmin', 'OrgAdmin', 'AssetManager'].includes(userRole)) && (
              <ChecklistItem
                label="View DPP Templates"
                description="Open the DPP Templates page, review an existing template's fields, or create a new template for a product category"
                onClick={() => navigateTo('dpp-templates')}
              />
            )}
            {(['SuperAdmin', 'OrgAdmin', 'AssetManager', 'FieldTech', 'Analyst'].includes(userRole)) && (
              <ChecklistItem
                label="Check IoT Devices"
                description="Open the IoT Dashboard, sort by battery level to find low-battery devices, and click one to view its sensor readings"
                onClick={() => navigateTo('iot')}
              />
            )}
            {(['SuperAdmin', 'OrgAdmin', 'AssetManager', 'FieldTech'].includes(userRole)) && (
              <ChecklistItem
                label="Create a Work Order"
                description="Create a test work order — pick an asset, set the type to Inspection, priority to Medium, and assign it to yourself"
                onClick={() => navigateTo('workorder-create')}
              />
            )}
            {(['SuperAdmin', 'OrgAdmin', 'AssetManager', 'Analyst'].includes(userRole)) && (
              <ChecklistItem
                label="Review ESG Metrics"
                description="Open ESG Reports, filter by a date range, sort by carbon saved, and try exporting the results to CSV"
                onClick={() => navigateTo('esg')}
              />
            )}
            {userRole === 'SuperAdmin' && (
              <ChecklistItem
                label="View Platform Overview"
                description="Open Platform Overview to see cross-organisation totals, then drill into a specific organisation's metrics"
                onClick={() => navigateTo('admin-dashboard')}
              />
            )}
            {(['SuperAdmin', 'OrgAdmin'].includes(userRole)) && (
              <ChecklistItem
                label="Review User Accounts"
                description="Open the Users page, check who has access to your organisation, and review their assigned roles"
                onClick={() => navigateTo('users')}
              />
            )}
          </div>
          <div className="bg-green-50 border border-green-300 rounded-lg p-4">
            <h4 className="font-semibold text-green-900 mb-2">Need Help?</h4>
            <p className="text-sm text-green-800 mb-3">
              This tutorial is always available from the <em>Tutorial</em> link in the sidebar under the <em>Help</em> section. You can revisit any step at any time, or contact your system administrator for further assistance.
            </p>
            <div className="flex gap-2">
              <button
                className="btn btn-primary"
                onClick={() => navigateTo('dashboard')}
              >
                Go to Dashboard
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => setCurrentStep(0)}
              >
                Restart Tutorial
              </button>
            </div>
          </div>
        </div>
      ),
    },
  ], [navigateTo, userRole])

  const steps = useMemo(() =>
    allSteps.filter(step => !step.roles || step.roles.includes(userRole)),
    [allSteps, userRole]
  )

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const handleJumpToStep = (index: number) => {
    setCurrentStep(index)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#0A4A8F] to-[#062E5C] text-white rounded-lg p-6 shadow-lg">
        <h1 className="text-3xl font-bold mb-2">Getting Started Tutorial</h1>
        <p className="text-blue-200">
          Learn how to use the Ping Group Digital Product Passport Platform effectively
        </p>
      </div>

      {/* Progress indicator */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">
            Step {currentStep + 1} of {steps.length}
          </span>
          <span className="text-sm text-gray-500">
            {Math.round(((currentStep + 1) / steps.length) * 100)}% Complete
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-esf-navy h-2 rounded-full transition-all duration-300"
            style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Step navigation (pills) */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-wrap gap-2">
          {steps.map((step, index) => (
            <button
              key={index}
              onClick={() => handleJumpToStep(index)}
              className={`px-3 py-1 rounded-full text-sm transition-colors ${
                index === currentStep
                  ? 'bg-esf-navy text-white font-semibold'
                  : index < currentStep
                  ? 'bg-green-100 text-green-700 hover:bg-green-200'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {index + 1}. {step.pill}
              {index < currentStep && ' \u2713'}
            </button>
          ))}
        </div>
      </div>

      {/* Current step content */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          {steps[currentStep].title}
        </h2>
        <div className="prose max-w-none">{steps[currentStep].content}</div>
      </div>

      {/* Navigation buttons */}
      <div className="flex justify-between items-center bg-white rounded-lg shadow p-4">
        <button
          onClick={handlePrevious}
          disabled={currentStep === 0}
          className={`btn ${
            currentStep === 0
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'btn-secondary'
          }`}
        >
          &larr; Previous
        </button>
        <span className="text-sm text-gray-600">
          Step {currentStep + 1} of {steps.length}
        </span>
        <button
          onClick={handleNext}
          disabled={currentStep === steps.length - 1}
          className={`btn ${
            currentStep === steps.length - 1
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'btn-primary'
          }`}
        >
          Next &rarr;
        </button>
      </div>

      {/* Quick links */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-semibold text-blue-900 mb-2">Quick Navigation</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <button
            onClick={() => navigateTo('dashboard')}
            className="text-sm text-blue-700 hover:text-blue-900 text-left"
          >
            &rarr; Dashboard
          </button>
          <button
            onClick={() => navigateTo('assets')}
            className="text-sm text-blue-700 hover:text-blue-900 text-left"
          >
            &rarr; Product Registry
          </button>
          <button
            onClick={() => navigateTo('workorders')}
            className="text-sm text-blue-700 hover:text-blue-900 text-left"
          >
            &rarr; Work Orders
          </button>
          <button
            onClick={() => navigateTo('iot')}
            className="text-sm text-blue-700 hover:text-blue-900 text-left"
          >
            &rarr; IoT Dashboard
          </button>
        </div>
      </div>
    </div>
  )
}

function ChecklistItem({ label, description, onClick }: { label: string; description: string; onClick: () => void }) {
  return (
    <label className="flex items-start gap-3 p-3 border rounded hover:bg-gray-50 cursor-pointer">
      <input type="checkbox" className="mt-1" />
      <div className="flex-1">
        <div className="font-semibold">{label}</div>
        <div className="text-sm text-gray-600">{description}</div>
      </div>
      <button
        onClick={(e) => { e.preventDefault(); onClick() }}
        className="text-xs text-esf-navy hover:text-esf-coral font-medium whitespace-nowrap"
      >
        Go &rarr;
      </button>
    </label>
  )
}
